import React, { useState, useEffect } from 'react';
import { API } from '../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

export const TICKET_CATEGORIES = [
  { value: 'student_complaint', label: 'Student Complaint' },
  { value: 'teacher_complaint', label: 'Teacher Complaint' },
  { value: 'payment_issue', label: 'Payment Issue' },
  { value: 'technical_issue', label: 'Technical Issue' },
  { value: 'academic_issue', label: 'Academic Issue' },
  { value: 'general_query', label: 'General Query' },
];
export const TICKET_PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const CreateTicketDialog = ({ presetStudentId, presetStudentName, onCreated, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [assignees, setAssignees] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [form, setForm] = useState({
    title: '', category: 'general_query', priority: 'medium', description: '',
    assigned_to: 'none', teacher_id: 'none', complaint_against: 'none',
  });

  useEffect(() => {
    if (open) {
      axios.get(`${API}/tickets/assignees`).then((r) => setAssignees(r.data)).catch(() => {});
      axios.get(`${API}/teachers`).then((r) => setTeachers(r.data)).catch(() => {});
    }
  }, [open]);

  const isComplaint = ['student_complaint', 'teacher_complaint'].includes(form.category);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error('Title and description required');
    try {
      await axios.post(`${API}/tickets`, {
        title: form.title,
        category: form.category,
        priority: form.priority,
        description: form.description,
        assigned_to: form.assigned_to === 'none' ? null : form.assigned_to,
        teacher_id: form.teacher_id === 'none' ? null : form.teacher_id,
        complaint_against: form.complaint_against === 'none' ? null : form.complaint_against,
        student_id: presetStudentId || null,
      });
      toast.success('Ticket created');
      setOpen(false);
      setForm({ title: '', category: 'general_query', priority: 'medium', description: '', assigned_to: 'none', teacher_id: 'none', complaint_against: 'none' });
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-ticket-button"><Plus className="w-4 h-4 mr-2" />Raise Ticket</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Raise Ticket / Complaint</DialogTitle>
          <DialogDescription>
            {presetStudentName ? `Regarding student: ${presetStudentName}` : 'Create a support ticket or complaint.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ticket-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TICKET_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TICKET_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {isComplaint && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Complaint Against</Label>
                <Select value={form.complaint_against} onValueChange={(v) => setForm({ ...form, complaint_against: v })}>
                  <SelectTrigger data-testid="ticket-against-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">N/A</SelectItem>
                    <SelectItem value="teacher">Teacher</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Related Teacher</Label>
                <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger data-testid="ticket-teacher-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div>
            <Label>Assign To (optional)</Label>
            <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
              <SelectTrigger data-testid="ticket-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.role.replace('_', ' ')})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="ticket-description-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="ticket-submit-button">Create Ticket</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTicketDialog;
